import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Dropdown, Form, Header, Icon, Input, Label, Message, Popup, Segment, Table, TextArea } from 'semantic-ui-react';
import { api } from '../api/apiClient';
import { dropdownGroups } from '../config/dropdownDefaults';
import { mapConfiguredDropdownGroups } from '../hooks/useDropdownOptions';

const LICENSE_PRODUCTS_GROUP = 'license_products';
const RESERVED_OPTION_FIELDS = ['id', 'key', 'text', 'value', 'sortOrder', 'isActive'];

const SELECTION_MODE_OPTIONS = [
    { key: 'perAddon', text: 'Quantity per add-on (M365 / Google Workspace style)', value: 'perAddon' },
    { key: 'productQuantity', text: 'Quantity on product (Server / Endpoint / MFT style)', value: 'productQuantity' }
];

let uidCounter = 0;
const nextUid = () => {
    uidCounter += 1;
    return `row_${uidCounter}`;
};

const stringifyValue = (value) => {
    if (typeof value === 'string') return value;
    return JSON.stringify(value);
};

const parseLooseJson = (value) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return '';

    try {
        return JSON.parse(trimmed);
    } catch {
        return trimmed;
    }
};

const extractMetadata = (option = {}) => Object.fromEntries(
    Object.entries(option).filter(([key]) => !RESERVED_OPTION_FIELDS.includes(key))
);

const toMetadataPairs = (metadata = {}) => Object.entries(metadata).map(([key, value]) => ({
    uid: nextUid(),
    key,
    value: typeof value === 'string' ? value : JSON.stringify(value)
}));

const pairsToMetadata = (pairs = []) => pairs.reduce((acc, pair) => {
    const key = String(pair.key || '').trim();
    if (!key) return acc;
    acc[key] = parseLooseJson(pair.value);
    return acc;
}, {});

const toProductForm = (metadata = {}) => {
    const { apiCategory, selectionMode, apiProductName, defaultAddonApiKeys, addons, ...extra } = metadata;
    const defaultKeys = Array.isArray(defaultAddonApiKeys) ? defaultAddonApiKeys : [];

    return {
        apiCategory: apiCategory || '',
        selectionMode: selectionMode || 'perAddon',
        apiProductName: apiProductName || '',
        extra,
        addons: (Array.isArray(addons) ? addons : []).map(addon => {
            const source = typeof addon === 'string' ? { name: addon } : (addon || {});
            return {
                uid: nextUid(),
                name: source.name || source.text || '',
                hidden: !!source.hidden,
                apiProductName: source.apiProductName || '',
                apiKey: source.apiKey || '',
                hasQuantity: !!source.hasQuantity,
                quantityField: source.quantityField || '',
                hasStorageGb: !!source.hasStorageGb,
                storageField: source.storageField || '',
                isDefault: !!source.apiKey && defaultKeys.includes(source.apiKey),
                subaddons: (Array.isArray(source.subaddons) ? source.subaddons : []).map(sub => ({
                    uid: nextUid(),
                    name: typeof sub === 'string' ? sub : (sub?.name || ''),
                    apiKey: typeof sub === 'string' ? '' : (sub?.apiKey || ''),
                    hidden: typeof sub === 'string' ? false : !!sub?.hidden
                }))
            };
        })
    };
};

const productFormToMetadata = (product) => {
    const isProductQuantity = product.selectionMode === 'productQuantity';

    const addons = (product.addons || [])
        .filter(addon => String(addon.name || '').trim())
        .map(addon => {
            const entry = {
                name: addon.name.trim(),
                ...(addon.hidden ? { hidden: true } : {})
            };

            if (isProductQuantity) {
                if (addon.apiKey.trim()) entry.apiKey = addon.apiKey.trim();
                if (addon.hasQuantity) {
                    entry.hasQuantity = true;
                    if (addon.quantityField.trim()) entry.quantityField = addon.quantityField.trim();
                }
            } else {
                if (addon.apiProductName.trim()) entry.apiProductName = addon.apiProductName.trim();
                const isEntra =
                    entry.apiProductName === 'microsoftEntra' ||
                    entry.name === 'Microsoft Entra';
                if (!isEntra && addon.hasStorageGb) {
                    entry.hasStorageGb = true;
                    if (String(addon.storageField || '').trim()) {
                        entry.storageField = addon.storageField.trim();
                    }
                }
                entry.subaddons = (addon.subaddons || [])
                    .filter(sub => String(sub.name || '').trim())
                    .map(sub => ({
                        name: sub.name.trim(),
                        ...(sub.apiKey.trim() ? { apiKey: sub.apiKey.trim() } : {}),
                        ...(sub.hidden ? { hidden: true } : {})
                    }));
            }

            return entry;
        });

    const defaultAddonApiKeys = isProductQuantity
        ? (product.addons || [])
            .filter(addon => addon.isDefault && String(addon.apiKey || '').trim())
            .map(addon => addon.apiKey.trim())
        : [];

    return {
        ...(product.extra || {}),
        ...(product.apiCategory.trim() ? { apiCategory: product.apiCategory.trim() } : {}),
        selectionMode: product.selectionMode,
        ...(isProductQuantity && product.apiProductName.trim()
            ? { apiProductName: product.apiProductName.trim() }
            : {}),
        ...(defaultAddonApiKeys.length ? { defaultAddonApiKeys } : {}),
        addons
    };
};

const createEmptyForm = (groupKey) => ({
    id: null,
    optionKey: '',
    label: '',
    value: '',
    sortOrder: 0,
    isActive: true,
    advanced: false,
    metadataText: '{}',
    pairs: [],
    product: toProductForm({})
});

const optionToForm = (option, groupKey) => {
    const metadata = extractMetadata(option);

    return {
        id: option.id,
        optionKey: option.key,
        label: option.text,
        value: stringifyValue(option.value),
        sortOrder: option.sortOrder || 0,
        isActive: option.isActive !== false,
        advanced: false,
        metadataText: JSON.stringify(metadata, null, 2),
        pairs: toMetadataPairs(metadata),
        product: toProductForm(groupKey === LICENSE_PRODUCTS_GROUP ? metadata : {})
    };
};

const LicenseProductSummary = ({ option }) => {
    const addons = Array.isArray(option.addons) ? option.addons : [];
    const isProductQuantity = option.selectionMode === 'productQuantity';

    return (
        <div>
            <Label size="tiny" color={isProductQuantity ? 'teal' : 'blue'} style={{ marginBottom: '0.35rem' }}>
                {isProductQuantity ? 'Quantity on product' : 'Quantity per add-on'}
            </Label>
            {addons.length === 0 ? (
                <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>No add-ons</div>
            ) : (
                <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.85rem', lineHeight: 1.5 }}>
                    {addons.map(addon => {
                        const subaddons = Array.isArray(addon?.subaddons) ? addon.subaddons : [];
                        return (
                            <li key={addon?.name}>
                                {addon?.name}
                                {addon?.hidden && (
                                    <Label size="mini" basic color="grey" style={{ marginLeft: '0.35rem' }}>
                                        Hidden
                                    </Label>
                                )}
                                {addon?.hasQuantity && (
                                    <span style={{ color: '#6b7280' }}> (with quantity)</span>
                                )}
                                {addon?.hasStorageGb && (
                                    <span style={{ color: '#6b7280' }}> (with storage GB)</span>
                                )}
                                {subaddons.length > 0 && (
                                    <span style={{ color: '#6b7280' }}>
                                        {` — ${subaddons.map(sub => {
                                            const name = typeof sub === 'string' ? sub : sub?.name;
                                            return typeof sub !== 'string' && sub?.hidden ? `${name} (hidden)` : name;
                                        }).join(', ')}`}
                                    </span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

const GenericMetadataSummary = ({ metadata }) => {
    const entries = Object.entries(metadata);
    if (entries.length === 0) {
        return <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>—</span>;
    }

    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
            {entries.map(([key, value]) => {
                const rendered = typeof value === 'string' ? value : JSON.stringify(value);
                const isLong = rendered.length > 28;
                const chip = (
                    <Label key={key} size="tiny" basic>
                        {key}
                        <Label.Detail>{isLong ? `${rendered.slice(0, 28)}…` : rendered}</Label.Detail>
                    </Label>
                );
                return isLong
                    ? <Popup key={key} trigger={chip} content={rendered} wide position="top center" />
                    : chip;
            })}
        </div>
    );
};

const SubaddonEditor = ({ subaddons, onChange }) => (
    <div style={{ marginTop: '0.5rem', paddingLeft: '0.75rem', borderLeft: '2px solid #e5e7eb' }}>
        <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.35rem' }}>
            Sub add-ons
        </div>
        {subaddons.length === 0 && (
            <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginBottom: '0.35rem' }}>
                None yet.
            </div>
        )}
        {subaddons.map((sub, index) => (
            <div
                key={sub.uid}
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto auto',
                    gap: '0.4rem',
                    marginBottom: '0.35rem',
                    alignItems: 'center',
                    opacity: sub.hidden ? 0.6 : 1
                }}
            >
                <Input
                    size="small"
                    placeholder="Sub add-on name"
                    value={sub.name}
                    onChange={(e) => onChange(subaddons.map((item, i) => (
                        i === index ? { ...item, name: e.target.value } : item
                    )))}
                    fluid
                    style={{ minWidth: 0 }}
                />
                <Input
                    size="small"
                    placeholder="API key (e.g. eDiscoverySearch)"
                    value={sub.apiKey}
                    onChange={(e) => onChange(subaddons.map((item, i) => (
                        i === index ? { ...item, apiKey: e.target.value } : item
                    )))}
                    fluid
                    style={{ minWidth: 0 }}
                />
                <Button
                    type="button"
                    size="small"
                    basic
                    icon={sub.hidden ? 'eye' : 'eye slash'}
                    title={sub.hidden ? 'Show in License Generator' : 'Hide from License Generator'}
                    style={{
                        background: '#fff',
                        color: sub.hidden ? '#16a34a' : '#4b5563'
                    }}
                    onClick={() => onChange(subaddons.map((item, i) => (
                        i === index ? { ...item, hidden: !item.hidden } : item
                    )))}
                />
                <Button
                    type="button"
                    size="small"
                    basic
                    icon="trash"
                    title="Delete sub add-on"
                    style={{ background: '#fff', color: '#dc2626' }}
                    onClick={() => onChange(subaddons.filter((_, i) => i !== index))}
                />
            </div>
        ))}
        <Button
            type="button"
            size="tiny"
            basic
            onClick={() => onChange([...subaddons, {
                uid: nextUid(),
                name: '',
                apiKey: '',
                hidden: false
            }])}
        >
            <Icon name="plus" />
            Add sub add-on
        </Button>
    </div>
);

const LicenseProductEditor = ({ product, onChange }) => {
    const isProductQuantity = product.selectionMode === 'productQuantity';

    const updateAddon = (index, changes) => onChange({
        ...product,
        addons: product.addons.map((addon, i) => (i === index ? { ...addon, ...changes } : addon))
    });

    return (
        <Segment style={{ borderRadius: '10px' }}>
            <Header as="h5" style={{ marginBottom: '0.75rem' }}>
                <Icon name="cubes" />
                Product structure
            </Header>

            <Form.Group widths="equal">
                <Form.Field>
                    <label>Quantity style</label>
                    <Dropdown
                        selection
                        fluid
                        options={SELECTION_MODE_OPTIONS}
                        value={product.selectionMode}
                        onChange={(e, { value }) => onChange({ ...product, selectionMode: value })}
                    />
                </Form.Field>
                <Form.Input
                    label="API category"
                    placeholder="e.g. endpoint"
                    value={product.apiCategory}
                    onChange={(e) => onChange({ ...product, apiCategory: e.target.value })}
                />
                {isProductQuantity && (
                    <Form.Input
                        label="API product name"
                        placeholder="e.g. endpointBackup"
                        value={product.apiProductName}
                        onChange={(e) => onChange({ ...product, apiProductName: e.target.value })}
                    />
                )}
            </Form.Group>

            {!product.apiCategory.trim() && (
                <Message size="tiny" warning>
                    Without an API category this product is shown on the form but never sent to the license API.
                </Message>
            )}

            <div style={{ fontWeight: 600, margin: '0.75rem 0 0.5rem' }}>Add-ons</div>

            {product.addons.length === 0 && (
                <Message size="tiny" info>
                    No add-ons yet. Add at least one so the product can be selected on the License Generator form.
                </Message>
            )}

            {product.addons.map((addon, index) => (
                <Segment
                    key={addon.uid}
                    secondary
                    style={{ borderRadius: '8px', opacity: addon.hidden ? 0.62 : 1 }}
                >
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                            gap: '0.6rem',
                            alignItems: 'end'
                        }}
                    >
                        <Form.Input
                            label="Add-on name"
                            placeholder="e.g. OneDrive"
                            value={addon.name}
                            onChange={(e) => updateAddon(index, { name: e.target.value })}
                            style={{ minWidth: 0, margin: 0 }}
                        />
                        {isProductQuantity ? (
                            <Form.Input
                                label="API key"
                                placeholder="e.g. sqlBackup"
                                value={addon.apiKey}
                                onChange={(e) => updateAddon(index, { apiKey: e.target.value })}
                                style={{ minWidth: 0, margin: 0 }}
                            />
                        ) : (
                            <Form.Input
                                label="API product name"
                                placeholder="e.g. oneDrive"
                                value={addon.apiProductName}
                                onChange={(e) => updateAddon(index, { apiProductName: e.target.value })}
                                style={{ minWidth: 0, margin: 0 }}
                            />
                        )}
                        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <Button
                                type="button"
                                basic
                                icon={addon.hidden ? 'eye' : 'eye slash'}
                                content={addon.hidden ? 'Show' : 'Hide'}
                                style={{
                                    background: '#fff',
                                    color: addon.hidden ? '#16a34a' : '#4b5563'
                                }}
                                onClick={() => updateAddon(index, { hidden: !addon.hidden })}
                            />
                            <Button
                                type="button"
                                basic
                                icon="trash"
                                content="Remove"
                                style={{ background: '#fff', color: '#dc2626' }}
                                onClick={() => onChange({
                                    ...product,
                                    addons: product.addons.filter((_, i) => i !== index)
                                })}
                            />
                        </div>
                    </div>

                    {isProductQuantity && (
                        <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
                            <Checkbox
                                label="Asks for its own quantity"
                                checked={addon.hasQuantity}
                                onChange={(e, { checked }) => updateAddon(index, { hasQuantity: checked })}
                            />
                            {addon.hasQuantity && (
                                <Input
                                    size="small"
                                    placeholder="Quantity payload field (e.g. miniCloudsPackQuantity)"
                                    value={addon.quantityField}
                                    onChange={(e) => updateAddon(index, { quantityField: e.target.value })}
                                    style={{ flex: '1 1 300px' }}
                                />
                            )}
                            <Checkbox
                                label="Send by default when nothing is selected"
                                checked={addon.isDefault}
                                onChange={(e, { checked }) => updateAddon(index, { isDefault: checked })}
                            />
                        </div>
                    )}

                    {!isProductQuantity && (
                        <>
                            {!(
                                String(addon.apiProductName || '').trim() === 'microsoftEntra' ||
                                String(addon.name || '').trim() === 'Microsoft Entra'
                            ) && (
                                <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
                                    <Checkbox
                                        label="Asks for Storage (GB)"
                                        checked={addon.hasStorageGb}
                                        onChange={(e, { checked }) => updateAddon(index, { hasStorageGb: checked })}
                                    />
                                    {addon.hasStorageGb && (
                                        <Input
                                            size="small"
                                            placeholder="Storage payload field (e.g. storageGb)"
                                            value={addon.storageField}
                                            onChange={(e) => updateAddon(index, { storageField: e.target.value })}
                                            style={{ flex: '1 1 300px' }}
                                        />
                                    )}
                                </div>
                            )}
                            <SubaddonEditor
                                subaddons={addon.subaddons}
                                onChange={(subaddons) => updateAddon(index, { subaddons })}
                            />
                        </>
                    )}
                </Segment>
            ))}

            <Button
                type="button"
                basic
                onClick={() => onChange({
                    ...product,
                    addons: [...product.addons, {
                        uid: nextUid(),
                        name: '',
                        hidden: false,
                        apiProductName: '',
                        apiKey: '',
                        hasQuantity: false,
                        quantityField: '',
                        hasStorageGb: false,
                        storageField: '',
                        isDefault: false,
                        subaddons: []
                    }]
                })}
            >
                <Icon name="plus" />
                Add add-on
            </Button>
        </Segment>
    );
};

const GenericMetadataEditor = ({ pairs, onChange }) => (
    <Segment style={{ borderRadius: '10px' }}>
        <Header as="h5" style={{ marginBottom: '0.75rem' }}>
            <Icon name="tags" />
            Extra fields
        </Header>
        {pairs.length === 0 && (
            <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                None. Most groups do not need any.
            </div>
        )}
        {pairs.map((pair, index) => (
            <div key={pair.uid} style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' }}>
                <Input
                    size="small"
                    placeholder="Field name"
                    value={pair.key}
                    onChange={(e) => onChange(pairs.map((item, i) => (
                        i === index ? { ...item, key: e.target.value } : item
                    )))}
                    style={{ flex: '1 1 35%' }}
                />
                <Input
                    size="small"
                    placeholder="Field value"
                    value={pair.value}
                    onChange={(e) => onChange(pairs.map((item, i) => (
                        i === index ? { ...item, value: e.target.value } : item
                    )))}
                    style={{ flex: '1 1 55%' }}
                />
                <Button
                    type="button"
                    size="small"
                    basic
                    color="red"
                    icon="trash"
                    onClick={() => onChange(pairs.filter((_, i) => i !== index))}
                />
            </div>
        ))}
        <Button
            type="button"
            size="tiny"
            basic
            onClick={() => onChange([...pairs, { uid: nextUid(), key: '', value: '' }])}
        >
            <Icon name="plus" />
            Add field
        </Button>
    </Segment>
);

const DropdownConfigurationManager = () => {
    const [selectedGroupKey, setSelectedGroupKey] = useState(dropdownGroups[0]?.key || '');
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [bulkSaving, setBulkSaving] = useState(false);
    const [togglingId, setTogglingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [formData, setFormData] = useState(createEmptyForm(selectedGroupKey));
    const [checkedIds, setCheckedIds] = useState([]);
    const formRef = useRef(null);
    const [decommissionRawEnabled, setDecommissionRawEnabled] = useState(true);
    const [decommissionRawLoading, setDecommissionRawLoading] = useState(false);
    const [decommissionRawSaving, setDecommissionRawSaving] = useState(false);
    const [decommissionRawMessage, setDecommissionRawMessage] = useState('');
    const [exportExcelEnabled, setExportExcelEnabled] = useState(true);
    const [exportExcelLoading, setExportExcelLoading] = useState(false);
    const [exportExcelSaving, setExportExcelSaving] = useState(false);
    const [exportExcelMessage, setExportExcelMessage] = useState('');

    const isLicenseProducts = selectedGroupKey === LICENSE_PRODUCTS_GROUP;
    const isLicenseGroup = selectedGroupKey.startsWith('license_');

    const selectedGroup = useMemo(
        () => groups.find(group => group.key === selectedGroupKey) || dropdownGroups.find(group => group.key === selectedGroupKey),
        [groups, selectedGroupKey]
    );

    const optionsByGroup = useMemo(() => mapConfiguredDropdownGroups(groups), [groups]);
    const selectedOptions = optionsByGroup[selectedGroupKey] || [];

    const groupOptions = useMemo(() => dropdownGroups.map(group => ({
        key: group.key,
        text: group.name,
        value: group.key
    })), []);

    const fetchOptions = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await api.dropdownOptions.getAll(null, true);
            setGroups(response.data?.groups || []);
        } catch (err) {
            console.error('Error fetching dropdown configuration:', err);
            setError(err.response?.data?.message || 'Failed to load dropdown configuration.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOptions();
    }, []);

    useEffect(() => {
        const fetchDecommissionRawSetting = async () => {
            setDecommissionRawLoading(true);
            try {
                const response = await api.system.getDecommissionStepStatusRawEnabled();
                setDecommissionRawEnabled(!!response?.data?.enabled);
            } catch (err) {
                console.error('Error fetching decommission raw button setting:', err);
                setDecommissionRawEnabled(true);
            } finally {
                setDecommissionRawLoading(false);
            }
        };
        fetchDecommissionRawSetting();
    }, []);

    useEffect(() => {
        const fetchExportExcelSetting = async () => {
            setExportExcelLoading(true);
            try {
                const response = await api.system.getDecommissionPendingFinalExportExcelEnabled();
                setExportExcelEnabled(!!response?.data?.enabled);
            } catch (err) {
                console.error('Error fetching decommission export excel setting:', err);
                setExportExcelEnabled(true);
            } finally {
                setExportExcelLoading(false);
            }
        };
        fetchExportExcelSetting();
    }, []);

    useEffect(() => {
        if (!decommissionRawMessage) return undefined;
        const timer = setTimeout(() => setDecommissionRawMessage(''), 3000);
        return () => clearTimeout(timer);
    }, [decommissionRawMessage]);

    useEffect(() => {
        if (!exportExcelMessage) return undefined;
        const timer = setTimeout(() => setExportExcelMessage(''), 3000);
        return () => clearTimeout(timer);
    }, [exportExcelMessage]);

    const handleDecommissionRawToggle = async (enabled) => {
        setDecommissionRawSaving(true);
        setDecommissionRawMessage('');
        try {
            const response = await api.admin.setDecommissionStepStatusRawEnabled(enabled);
            const isEnabled = !!response?.data?.enabled;
            setDecommissionRawEnabled(isEnabled);
            window.dispatchEvent(new CustomEvent('tb:decommissionStepStatusRawEnabledChanged', {
                detail: { enabled: isEnabled }
            }));
            setDecommissionRawMessage(isEnabled
                ? 'Decommission step status Raw button is now enabled.'
                : 'Decommission step status Raw button is now disabled.');
        } catch (err) {
            console.error('Error updating decommission raw button setting:', err);
            setDecommissionRawMessage(err.response?.data?.message || 'Failed to update decommission raw button setting.');
        } finally {
            setDecommissionRawSaving(false);
        }
    };

    const handleExportExcelToggle = async (enabled) => {
        setExportExcelSaving(true);
        setExportExcelMessage('');
        try {
            const response = await api.admin.setDecommissionPendingFinalExportExcelEnabled(enabled);
            const isEnabled = !!response?.data?.enabled;
            setExportExcelEnabled(isEnabled);
            window.dispatchEvent(new CustomEvent('tb:decommissionPendingFinalExportExcelEnabledChanged', {
                detail: { enabled: isEnabled }
            }));
            setExportExcelMessage(isEnabled
                ? 'Pending Final Approvals Export as Excel is now enabled.'
                : 'Pending Final Approvals Export as Excel is now disabled.');
        } catch (err) {
            console.error('Error updating decommission export excel setting:', err);
            setExportExcelMessage(err.response?.data?.message || 'Failed to update export excel setting.');
        } finally {
            setExportExcelSaving(false);
        }
    };

    useEffect(() => {
        setFormData(createEmptyForm(selectedGroupKey));
        setCheckedIds([]);
        setSuccess('');
        setError('');
    }, [selectedGroupKey]);

    const buildPayload = (option, overrides = {}) => ({
        groupKey: selectedGroupKey,
        optionKey: option.key,
        label: option.text,
        value: option.value,
        metadata: extractMetadata(option),
        sortOrder: option.sortOrder || 0,
        isActive: option.isActive !== false,
        ...overrides
    });

    const handleEdit = (option) => {
        setFormData(optionToForm(option, selectedGroupKey));
        setSuccess('');
        setError('');
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleCancelEdit = () => {
        setFormData(createEmptyForm(selectedGroupKey));
        setSuccess('');
        setError('');
    };

    const buildMetadataFromForm = () => {
        if (formData.advanced) {
            const trimmed = formData.metadataText.trim();
            if (!trimmed) return {};
            return JSON.parse(trimmed);
        }
        if (isLicenseProducts) {
            return productFormToMetadata(formData.product);
        }
        return pairsToMetadata(formData.pairs);
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            let metadata;
            try {
                metadata = buildMetadataFromForm();
            } catch {
                setError('Advanced JSON is not valid. Fix it or switch back to the form view.');
                setSaving(false);
                return;
            }

            const label = formData.label.trim();
            const rawValue = formData.value.trim();
            const payload = {
                groupKey: selectedGroupKey,
                optionKey: formData.optionKey.trim(),
                label,
                value: rawValue ? parseLooseJson(rawValue) : (isLicenseGroup ? label : ''),
                metadata,
                sortOrder: Number(formData.sortOrder) || 0,
                isActive: formData.isActive
            };

            if (!payload.label || payload.value === '') {
                setError('Label and value are required.');
                setSaving(false);
                return;
            }

            if (formData.id) {
                await api.admin.updateDropdownOption(formData.id, payload);
                setSuccess('Dropdown option updated.');
            } else {
                await api.admin.createDropdownOption(payload);
                setSuccess('Dropdown option added.');
            }

            setFormData(createEmptyForm(selectedGroupKey));
            await fetchOptions();
        } catch (err) {
            console.error('Error saving dropdown option:', err);
            setError(err.response?.data?.message || 'Failed to save dropdown option.');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (option, isActive) => {
        setTogglingId(option.id);
        setError('');
        setSuccess('');
        try {
            await api.admin.updateDropdownOption(option.id, buildPayload(option, { isActive }));
            await fetchOptions();
        } catch (err) {
            console.error('Error updating dropdown option:', err);
            setError(err.response?.data?.message || 'Failed to update dropdown option.');
        } finally {
            setTogglingId(null);
        }
    };

    const handleBulkSetActive = async (isActive) => {
        const targets = selectedOptions.filter(option => checkedIds.includes(option.id));
        if (targets.length === 0) return;

        setBulkSaving(true);
        setError('');
        setSuccess('');
        try {
            for (const option of targets) {
                await api.admin.updateDropdownOption(option.id, buildPayload(option, { isActive }));
            }
            setSuccess(`${targets.length} option${targets.length > 1 ? 's' : ''} ${isActive ? 'enabled' : 'disabled'}.`);
            setCheckedIds([]);
            await fetchOptions();
        } catch (err) {
            console.error('Error updating dropdown options:', err);
            setError(err.response?.data?.message || 'Failed to update selected options.');
        } finally {
            setBulkSaving(false);
        }
    };

    const handleDelete = async (option) => {
        setDeletingId(option.id);
        setError('');
        setSuccess('');
        try {
            await api.admin.deleteDropdownOption(option.id);
            setSuccess('Dropdown option deleted.');
            await fetchOptions();
            if (formData.id === option.id) {
                setFormData(createEmptyForm(selectedGroupKey));
            }
        } catch (err) {
            console.error('Error deleting dropdown option:', err);
            setError(err.response?.data?.message || 'Failed to delete dropdown option.');
        } finally {
            setDeletingId(null);
        }
    };

    const allChecked = selectedOptions.length > 0 && checkedIds.length === selectedOptions.length;
    const busy = saving || bulkSaving || !!togglingId;

    return (
        <div style={{ padding: '2rem' }}>
            <Header as="h2" style={{ marginBottom: '1rem', color: '#35B7F4' }}>
                <Icon name="list alternate outline" />
                Dropdown Configuration
            </Header>
            <p style={{ color: '#4b5563', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                Manage dropdown values centrally. Use the toggles to enable or disable options, and the form to add or
                edit them. Changes are loaded by application dropdowns at runtime, with source-code defaults retained
                as a fallback.
            </p>

<Segment
                loading={decommissionRawLoading}
                style={{
                    marginBottom: '1.5rem',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05)',
                    padding: '1.2rem'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '1rem',
                        flexWrap: 'wrap',
                        marginBottom: '0.8rem'
                    }}
                >
                    <Header as="h3" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Icon name="code" color={decommissionRawEnabled ? 'green' : 'grey'} />
                        Decommission Step Status — Raw Button
                    </Header>
                    <Label
                        style={{
                            margin: 0,
                            padding: '0.55em 0.9em',
                            fontWeight: 700,
                            letterSpacing: '0.02em',
                            background: decommissionRawEnabled ? '#dcfce7' : '#f3f4f6',
                            color: decommissionRawEnabled ? '#166534' : '#4b5563',
                            border: decommissionRawEnabled ? '1px solid #bbf7d0' : '1px solid #d1d5db'
                        }}
                    >
                        {decommissionRawEnabled ? 'ENABLED' : 'DISABLED'}
                    </Label>
                </div>

                <p style={{ color: '#4b5563', marginBottom: '1.1rem', lineHeight: 1.6 }}>
                    Control visibility of the Raw button in the decommission step status section on request details.
                </p>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '0.75rem'
                    }}
                >
                    <Button
                        size="large"
                        loading={decommissionRawSaving}
                        disabled={decommissionRawSaving || decommissionRawEnabled}
                        onClick={() => handleDecommissionRawToggle(true)}
                        style={{
                            borderRadius: '10px',
                            fontWeight: 700,
                            background: decommissionRawEnabled ? '#f8fafc' : '#16a34a',
                            color: decommissionRawEnabled ? '#94a3b8' : '#ffffff',
                            border: decommissionRawEnabled ? '1px solid #e2e8f0' : '1px solid #15803d'
                        }}
                    >
                        <Icon name="toggle on" />
                        Enable Raw Button
                    </Button>
                    <Button
                        size="large"
                        loading={decommissionRawSaving}
                        disabled={decommissionRawSaving || !decommissionRawEnabled}
                        onClick={() => handleDecommissionRawToggle(false)}
                        style={{
                            borderRadius: '10px',
                            fontWeight: 700,
                            background: decommissionRawEnabled ? '#475569' : '#f8fafc',
                            color: decommissionRawEnabled ? '#ffffff' : '#94a3b8',
                            border: decommissionRawEnabled ? '1px solid #334155' : '1px solid #e2e8f0'
                        }}
                    >
                        <Icon name="toggle off" />
                        Disable Raw Button
                    </Button>
                </div>

                {decommissionRawMessage && (
                    <Message
                        style={{ marginTop: '1rem', borderRadius: '10px' }}
                        positive={
                            decommissionRawMessage.toLowerCase().includes('enabled') ||
                            decommissionRawMessage.toLowerCase().includes('disabled')
                        }
                        negative={decommissionRawMessage.toLowerCase().includes('failed')}
                    >
                        {decommissionRawMessage}
                    </Message>
                )}
            </Segment>

            <Segment
                loading={exportExcelLoading}
                style={{
                    marginBottom: '1.5rem',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05)',
                    padding: '1.2rem'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '1rem',
                        flexWrap: 'wrap',
                        marginBottom: '0.8rem'
                    }}
                >
                    <Header as="h3" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Icon name="file excel outline" color={exportExcelEnabled ? 'green' : 'grey'} />
                        Pending Final Approvals — Export as Excel
                    </Header>
                    <Label
                        style={{
                            margin: 0,
                            padding: '0.55em 0.9em',
                            fontWeight: 700,
                            letterSpacing: '0.02em',
                            background: exportExcelEnabled ? '#dcfce7' : '#f3f4f6',
                            color: exportExcelEnabled ? '#166534' : '#4b5563',
                            border: exportExcelEnabled ? '1px solid #bbf7d0' : '1px solid #d1d5db'
                        }}
                    >
                        {exportExcelEnabled ? 'ENABLED' : 'DISABLED'}
                    </Label>
                </div>

                <p style={{ color: '#4b5563', marginBottom: '1.1rem', lineHeight: 1.6 }}>
                    Control visibility of the Export as Excel button in Multi Tenancy Decommission → Final Approval → Pending Final Approvals.
                </p>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '0.75rem'
                    }}
                >
                    <Button
                        size="large"
                        loading={exportExcelSaving}
                        disabled={exportExcelSaving || exportExcelEnabled}
                        onClick={() => handleExportExcelToggle(true)}
                        style={{
                            borderRadius: '10px',
                            fontWeight: 700,
                            background: exportExcelEnabled ? '#f8fafc' : '#16a34a',
                            color: exportExcelEnabled ? '#94a3b8' : '#ffffff',
                            border: exportExcelEnabled ? '1px solid #e2e8f0' : '1px solid #15803d'
                        }}
                    >
                        <Icon name="toggle on" />
                        Enable Export as Excel
                    </Button>
                    <Button
                        size="large"
                        loading={exportExcelSaving}
                        disabled={exportExcelSaving || !exportExcelEnabled}
                        onClick={() => handleExportExcelToggle(false)}
                        style={{
                            borderRadius: '10px',
                            fontWeight: 700,
                            background: exportExcelEnabled ? '#475569' : '#f8fafc',
                            color: exportExcelEnabled ? '#ffffff' : '#94a3b8',
                            border: exportExcelEnabled ? '1px solid #334155' : '1px solid #e2e8f0'
                        }}
                    >
                        <Icon name="toggle off" />
                        Disable Export as Excel
                    </Button>
                </div>

                {exportExcelMessage && (
                    <Message
                        style={{ marginTop: '1rem', borderRadius: '10px' }}
                        positive={
                            exportExcelMessage.toLowerCase().includes('enabled') ||
                            exportExcelMessage.toLowerCase().includes('disabled')
                        }
                        negative={exportExcelMessage.toLowerCase().includes('failed')}
                    >
                        {exportExcelMessage}
                    </Message>
                )}
            </Segment>

                        <Segment loading={loading} style={{ borderRadius: '12px' }}>
                <Form>
                    <Form.Field>
                        <label>Dropdown Group</label>
                        <Dropdown
                            selection
                            fluid
                            options={groupOptions}
                            value={selectedGroupKey}
                            onChange={(e, { value }) => setSelectedGroupKey(value)}
                        />
                    </Form.Field>
                </Form>

                {selectedGroup?.description && (
                    <Message info style={{ marginTop: '1rem' }}>
                        {selectedGroup.description}
                    </Message>
                )}

                {error && <Message negative>{error}</Message>}
                {success && <Message positive>{success}</Message>}

                <div ref={formRef} />
                <Segment secondary style={{ marginTop: '1.25rem', borderRadius: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <Header as="h4" style={{ margin: 0 }}>
                            {formData.id ? 'Edit Option' : 'Add Option'}
                        </Header>
                        <Checkbox
                            toggle
                            label="Advanced (raw JSON)"
                            checked={formData.advanced}
                            onChange={(e, { checked }) => setFormData(prev => {
                                if (!checked) {
                                    // Carry any raw JSON edits back into the form view
                                    try {
                                        const metadata = JSON.parse(prev.metadataText.trim() || '{}');
                                        return {
                                            ...prev,
                                            advanced: false,
                                            pairs: toMetadataPairs(metadata),
                                            product: isLicenseProducts ? toProductForm(metadata) : prev.product
                                        };
                                    } catch {
                                        setError('Advanced JSON is not valid, so it was discarded.');
                                        return { ...prev, advanced: false };
                                    }
                                }

                                const metadata = isLicenseProducts
                                    ? productFormToMetadata(prev.product)
                                    : pairsToMetadata(prev.pairs);
                                return {
                                    ...prev,
                                    advanced: true,
                                    metadataText: JSON.stringify(metadata, null, 2)
                                };
                            })}
                        />
                    </div>

                    <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} style={{ marginTop: '1rem' }}>
                        <Form.Group widths="equal">
                            <Form.Input
                                label="Option Key"
                                placeholder="auto-generated if blank"
                                value={formData.optionKey}
                                onChange={(e) => setFormData(prev => ({ ...prev, optionKey: e.target.value }))}
                                disabled={!!formData.id}
                            />
                            <Form.Input
                                required
                                label="Label"
                                placeholder="Display label"
                                value={formData.label}
                                onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                            />
                            <Form.Input
                                required={!isLicenseGroup}
                                label="Value"
                                placeholder={isLicenseGroup ? 'same as label if left blank' : 'Submitted value'}
                                value={formData.value}
                                onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                            />
                            <Form.Input
                                label="Sort Order"
                                type="number"
                                value={formData.sortOrder}
                                onChange={(e) => setFormData(prev => ({ ...prev, sortOrder: e.target.value }))}
                            />
                        </Form.Group>

                        {formData.advanced ? (
                            <Form.Field>
                                <label>Metadata JSON</label>
                                <TextArea
                                    value={formData.metadataText}
                                    onChange={(e) => setFormData(prev => ({ ...prev, metadataText: e.target.value }))}
                                    style={{ minHeight: '160px', fontFamily: 'monospace' }}
                                />
                            </Form.Field>
                        ) : isLicenseProducts ? (
                            <LicenseProductEditor
                                product={formData.product}
                                onChange={(product) => setFormData(prev => ({ ...prev, product }))}
                            />
                        ) : (
                            <GenericMetadataEditor
                                pairs={formData.pairs}
                                onChange={(pairs) => setFormData(prev => ({ ...prev, pairs }))}
                            />
                        )}

                        <Form.Field style={{ marginTop: '1rem' }}>
                            <Checkbox
                                toggle
                                label={isLicenseProducts
                                    ? 'Show this product in License Generator'
                                    : 'Show this option in the application'}
                                checked={formData.isActive}
                                onChange={(e, { checked }) => setFormData(prev => ({ ...prev, isActive: checked }))}
                            />
                        </Form.Field>
                        <Button primary type="submit" loading={saving} disabled={busy}>
                            <Icon name={formData.id ? 'save' : 'plus'} />
                            {formData.id ? 'Save Option' : 'Add Option'}
                        </Button>
                        {formData.id && (
                            <Button type="button" onClick={handleCancelEdit} disabled={busy}>
                                Cancel
                            </Button>
                        )}
                    </Form>
                </Segment>

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    flexWrap: 'wrap',
                    marginTop: '1.25rem'
                }}>
                    <span style={{ color: '#4b5563', fontSize: '0.9rem' }}>
                        {checkedIds.length > 0
                            ? `${checkedIds.length} selected`
                            : 'Select rows to enable or disable them together'}
                    </span>
                    <Button
                        size="small"
                        className="tb-bulk-enable-btn"
                        icon="check"
                        content="Enable selected"
                        disabled={checkedIds.length === 0 || bulkSaving}
                        loading={bulkSaving}
                        onClick={() => handleBulkSetActive(true)}
                    />
                    <Button
                        size="small"
                        className="tb-bulk-disable-btn"
                        icon="ban"
                        content="Disable selected"
                        disabled={checkedIds.length === 0 || bulkSaving}
                        onClick={() => handleBulkSetActive(false)}
                    />
                </div>

                <Table celled striped selectable stackable style={{ marginTop: '0.75rem' }}>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell collapsing>
                                <Checkbox
                                    checked={allChecked}
                                    indeterminate={checkedIds.length > 0 && !allChecked}
                                    onChange={(e, { checked }) => setCheckedIds(
                                        checked ? selectedOptions.map(option => option.id) : []
                                    )}
                                />
                            </Table.HeaderCell>
                            <Table.HeaderCell>Label</Table.HeaderCell>
                            <Table.HeaderCell>Value</Table.HeaderCell>
                            <Table.HeaderCell>{isLicenseProducts ? 'Add-ons' : 'Details'}</Table.HeaderCell>
                            <Table.HeaderCell collapsing textAlign="center">Enabled</Table.HeaderCell>
                            <Table.HeaderCell textAlign="right">Actions</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {selectedOptions.length === 0 ? (
                            <Table.Row>
                                <Table.Cell colSpan={6} textAlign="center" style={{ color: '#6b7280' }}>
                                    No options configured for this group.
                                </Table.Cell>
                            </Table.Row>
                        ) : selectedOptions.map(option => (
                            <Table.Row key={option.id || option.key} disabled={option.isActive === false}>
                                <Table.Cell collapsing>
                                    <Checkbox
                                        checked={checkedIds.includes(option.id)}
                                        onChange={(e, { checked }) => setCheckedIds(prev => (
                                            checked
                                                ? [...prev, option.id]
                                                : prev.filter(id => id !== option.id)
                                        ))}
                                    />
                                </Table.Cell>
                                <Table.Cell>
                                    <strong>{option.text}</strong>
                                    <div style={{ color: '#9ca3af', fontSize: '0.78rem' }}>{option.key}</div>
                                </Table.Cell>
                                <Table.Cell>
                                    <code>{stringifyValue(option.value)}</code>
                                </Table.Cell>
                                <Table.Cell style={{ maxWidth: '380px' }}>
                                    {isLicenseProducts
                                        ? <LicenseProductSummary option={option} />
                                        : <GenericMetadataSummary metadata={extractMetadata(option)} />}
                                </Table.Cell>
                                <Table.Cell collapsing textAlign="center">
                                    <Checkbox
                                        toggle
                                        checked={option.isActive !== false}
                                        disabled={togglingId === option.id || bulkSaving}
                                        onChange={(e, { checked }) => handleToggleActive(option, checked)}
                                    />
                                </Table.Cell>
                                <Table.Cell textAlign="right">
                                    <Button size="small" icon="edit" content="Edit" onClick={() => handleEdit(option)} />
                                    <Button
                                        size="small"
                                        color="red"
                                        icon="trash"
                                        content="Delete"
                                        loading={deletingId === option.id}
                                        disabled={deletingId === option.id}
                                        onClick={() => handleDelete(option)}
                                    />
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </Segment>
        </div>
    );
};

export default DropdownConfigurationManager;
